import { Alert, Badge, Button, Center, Group, Loader, Stack, Text, Textarea, TextInput, Title } from '@mantine/core';
import type { UseFormReturnType } from '@mantine/form';
import { modals } from '@mantine/modals';
import { IconAlertTriangle } from '@tabler/icons-react';
import type { ReactNode } from 'react';
import { errorMessage } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { humanize } from '../lib/format';
import { STATUS_COLOR } from '../theme';

export function PageHeader({ title, description, actions }: { title: ReactNode; description?: ReactNode; actions?: ReactNode }) {
  return (
    <Group justify="space-between" align="flex-start" mb="lg" wrap="wrap" gap="sm">
      <Stack gap={2}>
        <Title order={2} fz={24}>
          {title}
        </Title>
        {description && (
          <Text c="dimmed" size="sm" maw={680}>
            {description}
          </Text>
        )}
      </Stack>
      {actions && <Group gap="xs">{actions}</Group>}
    </Group>
  );
}

export function StatusBadge({ status, label }: { status: string; label?: string }) {
  return <Badge color={STATUS_COLOR[status] ?? 'gray'}>{label ?? humanize(status)}</Badge>;
}

export function Loading() {
  return (
    <Center py={60}>
      <Loader size="sm" />
    </Center>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  return (
    <Alert color="red" icon={<IconAlertTriangle size={18} />} title="Couldn't load this" my="md">
      <Stack gap="xs" align="flex-start">
        <Text size="sm">{errorMessage(error)}</Text>
        {onRetry && (
          <Button size="xs" variant="light" color="red" onClick={onRetry}>
            Try again
          </Button>
        )}
      </Stack>
    </Alert>
  );
}

/** Shows its children only to staff with all the permissions. */
export function Can({ perm, any, children }: { perm?: string | string[]; any?: string[]; children: ReactNode }) {
  const { can, canAny } = useAuth();
  const all = perm === undefined ? [] : Array.isArray(perm) ? perm : [perm];
  if (!can(...all)) return null;
  if (any && !canAny(...any)) return null;
  return <>{children}</>;
}

/** A Hindi field and its English counterpart, side by side. Hindi is required; English falls back to it. */
export function L10nFields<T>({
  form,
  path,
  label,
  multiline,
  required = true,
  minRows = 3,
}: {
  form: UseFormReturnType<T>;
  path: string;
  label: string;
  multiline?: boolean;
  required?: boolean;
  minRows?: number;
}) {
  const Field = multiline ? Textarea : TextInput;
  const extra = multiline ? { autosize: true, minRows } : {};
  return (
    <Group grow align="flex-start" wrap="wrap" gap="sm">
      <Field label={`${label} (हिंदी)`} withAsterisk={required} className="sb-hindi" miw={240} {...extra} {...form.getInputProps(`${path}.hi`)} />
      <Field label={`${label} (English)`} description="Optional — the app shows the Hindi when empty" miw={240} {...extra} {...form.getInputProps(`${path}.en`)} />
    </Group>
  );
}

/** Asks before an action that can't be taken back. */
export function confirm({ title, message, confirmLabel, danger = true, onConfirm }: { title: string; message: ReactNode; confirmLabel: string; danger?: boolean; onConfirm: () => void }) {
  modals.openConfirmModal({
    title,
    children: <Text size="sm">{message}</Text>,
    labels: { confirm: confirmLabel, cancel: 'Cancel' },
    confirmProps: { color: danger ? 'red' : 'brand' },
    onConfirm,
  });
}

/** Asks for a short note (sending a story back, unpublishing). */
export function askForText({ title, label, confirmLabel, minLength = 3, onSubmit }: { title: string; label: string; confirmLabel: string; minLength?: number; onSubmit: (text: string) => void }) {
  let value = '';
  modals.openConfirmModal({
    title,
    children: <Textarea label={label} autosize minRows={3} data-autofocus onChange={event => (value = event.currentTarget.value)} />,
    labels: { confirm: confirmLabel, cancel: 'Cancel' },
    onConfirm: () => {
      if (value.trim().length >= minLength) onSubmit(value.trim());
    },
  });
}

/** An L10n value for a table cell: the Hindi, with the English below when there is one. */
export function L10nText({ value, size = 'sm' }: { value: { hi: string; en: string } | null | undefined; size?: string }) {
  if (!value) return <Text c="dimmed">—</Text>;
  return (
    <Stack gap={0}>
      <Text size={size} fw={500} className="sb-hindi" lineClamp={2}>
        {value.hi}
      </Text>
      {value.en && (
        <Text size="xs" c="dimmed" lineClamp={1}>
          {value.en}
        </Text>
      )}
    </Stack>
  );
}

/** Blank L10n for new forms. */
export const blank = () => ({ hi: '', en: '' });
